import React, { useState, useEffect } from 'react';
import { 
    Row, 
    Col, 
    Card, 
    Button, 
    Spinner, 
    Badge, 
    Modal, 
    Form,
    Alert,
    InputGroup,
    Dropdown,
    Container,
    Nav,
    ListGroup,
    OverlayTrigger,
    Tooltip
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faBoxOpen, 
    faPlus, 
    faEdit, 
    faPause, 
    faPlay,
    faTrash,
    faDollarSign,
    faUsers,
    faTimes,
    faCheck,
    faExclamationTriangle,
    faRobot,
    faDatabase,
    faSync,
    faChartLine,
    faCog,
    faLink,
    faQrcode,
    faImage,
    faEye,
    faPercent,
    faCreditCard,
    faHandshake,
    faLightbulb,
    faExclamationCircle,
    faBrain,
    faTools,
    faInfoCircle,
    faSave,
    faGlobe,
    faDownload
} from '@fortawesome/free-solid-svg-icons';
import { getHatateToken } from '../../helpers/utils';
import QRCode from 'qrcode';


// ==================== INTERFACES ====================
interface ProductSalesTools {
  urls: {
    mainProductUrl?: string;
    demoUrl?: string;
    documentationUrl?: string;
    supportUrl?: string;
  };
  qrCodes: {
    payment: {
      enabled: boolean;
      amount: number;
      currency: string;
      paymentMethod: 'stripe' | 'paypal' | 'mercadopago' | 'custom';
      customPaymentUrl?: string;
      generatedQR?: string;
    };
    discount: {
      enabled: boolean;
      discountType: 'percentage' | 'fixed';
      value: number;
      validUntil?: string;
      code?: string;
      generatedQR?: string;
    };
  };
  catalog: {
    images: string[];
    documents: string[];
    videos: string[];
    presentations: string[];
  };
  negotiationRules: {
    minPrice: number;
    maxDiscount: number;
    negotiationStyle: 'flexible' | 'firm' | 'aggressive';
    keySellingPoints: string[];
    objectionHandlers: Record<string, string>;
    closingTechniques: string[];
  };
}

interface Product {
    id: string;
    name: string;
    description: string;
    price: number;
    category: string;
    closingMethod: string;
    targetAudience: string;
    features: string[];
    isActive: boolean;
    salesCount: number;
    conversionRate: number;
    isAnubisProduct: boolean;
    visibleToUser: boolean;
    assignedAgent?: string;
    agentName?: string;
    createdAt: string;
    salesTools?: ProductSalesTools;
}

interface Agent {
    id: string;
    name: string;
    personality: string;
    isActive: boolean;
    currentAssignments: number;
    maxAssignments: number;
    successRate: number;
}

interface ProductFormData {
    name: string;
    description: string;
    price: number | string;
    category: string;
    closingMethod: string;
    targetAudience: string;
    features: string[];
    isActive: boolean;
    assignedAgent?: string;
    salesTools?: ProductSalesTools;
}

interface ProductsTabProps {
    language: 'es' | 'en';
    onProductsChange?: (products: Product[]) => void;
}

// ==================== ESTILOS CSS ====================
const customStyles = `
  .products-tab .product-card {
  transition: all 0.3s ease !important;
  border: 1px solid #e9ecef !important;
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075) !important;
}

.products-tab .product-card:hover {
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15) !important;
  transform: translateY(-2px) !important;
  z-index: 1 !important;
}

.products-tab .product-card:hover .dropdown.show {
  z-index: 1080 !important;
}

.products-tab .dropdown-menu {
  z-index: 1080 !important;
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15) !important;
  border: 1px solid rgba(0, 0, 0, 0.1) !important;
}

.products-tab .modal-dialog {
  max-width: 95vw !important;
  margin: 1rem auto !important;
}

.products-tab .modal-content {
  max-height: 90vh !important;
  overflow: hidden !important;
  display: flex !important;
  flex-direction: column !important;
}

.products-tab .modal-body {
  overflow-y: auto !important;
  flex: 1 1 auto !important;
  padding: 1rem !important;
}

.products-tab .modal-header,
.products-tab .modal-footer {
  flex-shrink: 0 !important;
}

@media (max-width: 768px) {
  .products-tab .modal-dialog {
    margin: 0.5rem !important;
    max-width: calc(100vw - 1rem) !important;
  }
}

@media (max-width: 576px) {
  .products-tab .modal-dialog {
    margin: 0.25rem !important;
    max-width: calc(100vw - 0.5rem) !important;
  }
  
  .products-tab .modal-body {
    padding: 0.75rem !important;
  }
}
`;

// ==================== UTILIDADES ====================
const getAuthHeaders = () => {
    const token = getHatateToken();
    return {
        'Content-Type': 'application/json',
        'Authorization': token || 'Bearer default-token',
        'x-api-key': 'anubis-sales-bot-api-key'
    };
};

const generateQRCode = async (data: string, type: 'payment' | 'discount'): Promise<string> => {
    try {
        const options = {
            width: 300,
            margin: 2,
            color: {
                dark: '#000000',
                light: '#FFFFFF'
            }
        };
        
        const qrDataURL = await QRCode.toDataURL(data, options);
        return qrDataURL;
    } catch (error) {
        console.error('Error generando QR:', error);
        return '';
    }
};

const downloadQRCode = (dataURL: string, filename: string) => {
    const link = document.createElement('a');
    link.download = filename;
    link.href = dataURL;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

const createDefaultSalesTools = (price: number = 0): ProductSalesTools => ({
    urls: {
        mainProductUrl: '',
        demoUrl: '',
        documentationUrl: '',
        supportUrl: ''
    },
    qrCodes: {
        payment: { 
            enabled: false, 
            amount: price, 
            currency: 'CLP', 
            paymentMethod: 'custom' 
        },
        discount: { 
            enabled: false, 
            discountType: 'percentage', 
            value: 0 
        }
    },
    catalog: { 
        images: [], 
        documents: [], 
        videos: [], 
        presentations: [] 
    },
    negotiationRules: {
        minPrice: Math.floor(price * 0.8),
        maxDiscount: 20,
        negotiationStyle: 'flexible',
        keySellingPoints: [],
        objectionHandlers: {},
        closingTechniques: []
    }
});

// ==================== HOOK PERSONALIZADO ====================
const useSellerbotProducts = () => {
    const [products, setProducts] = useState<Product[]>([]);
    const [agents, setAgents] = useState<Agent[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [initializingDB, setInitializingDB] = useState(false);

    const initializeDatabase = async () => {
        try {
            setInitializingDB(true);
            console.log('🔄 Inicializando base de datos...');
            
            const baseUrl = "https://backend.anubisai.net";
            const statusResponse = await fetch(`${baseUrl}/api/sellerbot/status`, {
                method: 'GET',
                headers: getAuthHeaders()
            });

            if (statusResponse.ok) {
                const setupData = await statusResponse.json();
                console.log('📊 Setup status:', setupData);
                
                if (!setupData.data?.checks?.database_initialized) {
                    console.log('🏗️ Base de datos no inicializada, forzando inicialización...');
                    
                    const statusResponse = await fetch(`${baseUrl}/api/sellerbot/status`, {
                        method: 'GET',
                        headers: getAuthHeaders()
                    });
                    
                    console.log('📡 Status response status:', statusResponse.status);
                }
            }
            
            return true;
        } catch (err) {
            console.error('❌ Error inicializando BD:', err);
            return false;
        } finally {
            setInitializingDB(false);
        }
    };

    const fetchAgents = async () => {
        try {
            const baseUrl = "https://backend.anubisai.net";
            const response = await fetch(`${baseUrl}/api/sellerbot/profiles`, {
                method: 'GET',
                headers: getAuthHeaders()
            });

            if (response.ok) {
                const contentType = response.headers.get('content-type');
                if (contentType?.includes('application/json')) {
                    const data = await response.json();
                    if (data.success && data.data?.profiles) {
                        const mappedAgents = data.data.profiles.map((profile: any) => ({
                            id: profile.id,
                            name: profile.name,
                            personality: profile.personality,
                            isActive: profile.isActive || profile.is_active,
                            currentAssignments: 0,
                            maxAssignments: 10,
                            successRate: profile.successRate || profile.success_rate || 0
                        }));
                        setAgents(mappedAgents);
                    }
                }
            }
        } catch (err) {
            console.error('❌ Error cargando agentes:', err);
        }
    };

    const fetchProducts = async (retryCount = 0) => {
        try {
            setLoading(true);
            setError(null);
            
            const baseUrl = "https://backend.anubisai.net";
            console.log('🌐 Base URL detected:', baseUrl);
            console.log('📡 Making request to:', `${baseUrl}/api/sellerbot/products`);
            
            const token = getHatateToken();
            console.log('🔑 Auth token:', token ? 'Present' : 'Missing');

            const response = await fetch(`${baseUrl}/api/sellerbot/products`, {
                method: 'GET',
                headers: getAuthHeaders(),
                credentials: 'include'
            });

            if (!response.ok) {
                const errorText = await response.text();
                console.error('❌ Response not OK:', response.status, response.statusText);
                console.error('❌ Error content:', errorText);
                
                if (errorText.includes('<!doctype html>') || errorText.includes('<html')) {
                    if (retryCount === 0) {
                        console.log('🔄 Detectado HTML, intentando inicializar BD...');
                        const initialized = await initializeDatabase();
                        if (initialized) {
                            await new Promise(resolve => setTimeout(resolve, 2000));
                            return fetchProducts(retryCount + 1);
                        }
                    }
                    throw new Error('Base de datos no inicializada. Contacte al administrador.');
                }
                
                throw new Error(`Error ${response.status}: ${response.statusText} - ${errorText}`);
            }

            const contentType = response.headers.get('content-type');
            console.log('📄 Content-Type received:', contentType);
            
            if (!contentType || !contentType.includes('application/json')) {
                const textResponse = await response.text();
                console.error('❌ Respuesta no es JSON:', textResponse.substring(0, 500));
                
                if (retryCount === 0) {
                    console.log('🔄 Intentando inicializar BD por respuesta no-JSON...');
                    await initializeDatabase();
                    await new Promise(resolve => setTimeout(resolve, 2000));
                    return fetchProducts(retryCount + 1);
                }
                
                throw new Error(`Servidor devolvió contenido no-JSON. Content-Type: ${contentType}`);
            }

            const data = await response.json();
            console.log('✅ JSON Response received:', data);
            
            if (data.success && data.data?.products) {
                const processedProducts = data.data.products.map((product: any) => {
                    let salesTools = null;
                    if (product.sales_tools) {
                        try {
                            salesTools = typeof product.sales_tools === 'string' 
                                ? JSON.parse(product.sales_tools) 
                                : product.sales_tools;
                        } catch (e) {
                            console.warn('Error parseando sales_tools para:', product.name);
                        }
                    }

                    return {
                        ...product,
                        salesTools: salesTools
                    };
                });

                setProducts(processedProducts);
                console.log('✅ Productos cargados:', processedProducts.length);
            } else {
                console.warn('⚠️ Response structure unexpected:', data);
                throw new Error(data.message || 'Error al cargar productos');
            }
        } catch (err: any) {
            console.error('❌ Error cargando productos:', err);
            setError(err.message || 'Error al cargar productos');
            setProducts([]);
        } finally {
            setLoading(false);
        }
    };

    const createProduct = async (productData: Omit<ProductFormData, 'isActive'> & { isActive?: boolean }) => {
        try {
            const baseUrl = "https://backend.anubisai.net";
            const response = await fetch(`${baseUrl}/api/sellerbot/products`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify({
                    name: productData.name,
                    description: productData.description,
                    price: Number(productData.price),
                    category: productData.category,
                    closing_method: productData.closingMethod,
                    target_audience: productData.targetAudience,
                    features: productData.features,
                    is_active: productData.isActive ?? true,
                    assigned_agent: productData.assignedAgent,
                    sales_tools: productData.salesTools ? JSON.stringify(productData.salesTools) : null
                })
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`Error ${response.status}: ${response.statusText} - ${errorText}`);
            }

            const contentType = response.headers.get('content-type');
            if (!contentType || !contentType.includes('application/json')) {
                const textResponse = await response.text();
                throw new Error(`Error del servidor: ${textResponse}`);
            }

            const data = await response.json();
            
            if (data.success) {
                console.log('✅ Producto creado:', data.data);
                await fetchProducts();
                return data.data;
            } else {
                throw new Error(data.message || 'Error al crear producto');
            }
        } catch (err: any) {
            console.error('❌ Error creando producto:', err);
            throw err;
        }
    };

    const updateProduct = async (productId: string, productData: ProductFormData) => {
        try {
            const baseUrl = "https://backend.anubisai.net";
            const response = await fetch(`${baseUrl}/api/sellerbot/products/${productId}`, {
                method: 'PUT',
                headers: getAuthHeaders(),
                body: JSON.stringify({
                    name: productData.name,
                    description: productData.description,
                    price: Number(productData.price),
                    category: productData.category,
                    closing_method: productData.closingMethod,
                    target_audience: productData.targetAudience,
                    features: productData.features,
                    is_active: productData.isActive,
                    assigned_agent: productData.assignedAgent,
                    sales_tools: productData.salesTools ? JSON.stringify(productData.salesTools) : null
                })
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`Error ${response.status}: ${response.statusText} - ${errorText}`);
            }

            const contentType = response.headers.get('content-type');
            if (!contentType || !contentType.includes('application/json')) {
                const textResponse = await response.text();
                throw new Error(`Error del servidor: ${textResponse}`);
            }

            const data = await response.json();
            
            if (data.success) {
                console.log('✅ Producto actualizado:', productId);
                await fetchProducts();
                return data.data;
            } else {
                throw new Error(data.message || 'Error al actualizar producto');
            }
        } catch (err: any) {
            console.error('❌ Error actualizando producto:', err);
            throw err;
        }
    };

    const deleteProduct = async (productId: string) => {
        try {
            const baseUrl = "https://backend.anubisai.net";
            const response = await fetch(`${baseUrl}/api/sellerbot/products/${productId}`, {
                method: 'DELETE',
                headers: getAuthHeaders()
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`Error ${response.status}: ${response.statusText} - ${errorText}`);
            }

            const contentType = response.headers.get('content-type');
            if (!contentType || !contentType.includes('application/json')) {
                const textResponse = await response.text();
                throw new Error(`Error del servidor: ${textResponse}`);
            }

            const data = await response.json();
            
            if (data.success) {
                console.log('✅ Producto eliminado:', productId);
                await fetchProducts();
                return productId;
            } else {
                throw new Error(data.message || 'Error al eliminar producto');
            }
        } catch (err: any) {
            console.error('❌ Error eliminando producto:', err);
            throw err;
        }
    };

    const toggleProductStatus = async (productId: string) => {
        const product = products.find(p => p.id === productId);
        if (product) {
            await updateProduct(productId, {
                name: product.name,
                description: product.description,
                price: product.price,
                category: product.category,
                closingMethod: product.closingMethod,
                targetAudience: product.targetAudience,
                features: product.features,
                isActive: !product.isActive,
                assignedAgent: product.assignedAgent,
                salesTools: product.salesTools
            });
        }
    };

    useEffect(() => {
        const loadData = async () => {
            await fetchAgents();
            await fetchProducts();
        };
        loadData();
    }, []);

    return {
        products,
        agents,
        loading,
        error,
        initializingDB,
        refetch: fetchProducts,
        createProduct,
        updateProduct,
        deleteProduct,
        toggleProductStatus,
        initializeDatabase
    };
};

// ==================== COMPONENTE PRINCIPAL ====================
const ProductsTab: React.FC<ProductsTabProps> = ({ language, onProductsChange }) => {
    const {
        products,
        agents,
        loading: productsLoading,
        error: productsError,
        initializingDB,
        refetch: refetchProducts,
        createProduct,
        updateProduct,
        deleteProduct,
        toggleProductStatus,
        initializeDatabase
    } = useSellerbotProducts();

    // Estados locales - MANTENER IGUAL AL ORIGINAL
    const [showProductModal, setShowProductModal] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
    const [productFormData, setProductFormData] = useState<ProductFormData>({
        name: '',
        description: '',
        price: '',
        category: 'product',
        closingMethod: 'link',
        targetAudience: '',
        features: [],
        isActive: true,
        assignedAgent: ''
    });
    const [newFeature, setNewFeature] = useState('');
    const [formLoading, setFormLoading] = useState(false);
    const [formError, setFormError] = useState<string | null>(null);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [productToDelete, setProductToDelete] = useState<Product | null>(null);

    // Estados para Sales Tools
    const [activeTab, setActiveTab] = useState('basic');
    const [newKeyPoint, setNewKeyPoint] = useState('');

    // Notificar cambios al componente padre
    useEffect(() => {
        if (onProductsChange) {
            onProductsChange(products);
        }
    }, [products, onProductsChange]);

    // ==================== FUNCIONES - MANTENER ORIGINALES ====================
    const handleInitializeDB = async () => {
        try {
            await initializeDatabase();
            await new Promise(resolve => setTimeout(resolve, 2000));
            await refetchProducts();
        } catch (err) {
            console.error('Error inicializando BD:', err);
        }
    };

    const handleShowProductModal = (product?: Product) => {
        if (product) {
            setSelectedProduct(product);
            setProductFormData({
                name: product.name,
                description: product.description,
                price: product.price,
                category: product.category,
                closingMethod: product.closingMethod,
                targetAudience: product.targetAudience,
                features: product.features,
                isActive: product.isActive,
                assignedAgent: product.assignedAgent || '',
                salesTools: product.salesTools || createDefaultSalesTools(product.price)
            });
        } else {
            setSelectedProduct(null);
            setProductFormData({
                name: '',
                description: '',
                price: '',
                category: 'product',
                closingMethod: 'link',
                targetAudience: '',
                features: [],
                isActive: true,
                assignedAgent: '',
                salesTools: createDefaultSalesTools(0)
            });
        }
        setShowProductModal(true);
        setFormError(null);
        setActiveTab('basic');
    };

    const handleSaveProduct = async () => {
        try {
            setFormLoading(true);
            setFormError(null);

            // Actualizar QR payment amount con precio actual
            if (productFormData.salesTools) {
                productFormData.salesTools.qrCodes.payment.amount = Number(productFormData.price);
                productFormData.salesTools.negotiationRules.minPrice = Math.floor(Number(productFormData.price) * 0.8);
            }

            if (selectedProduct) {
                await updateProduct(selectedProduct.id, productFormData);
            } else {
                await createProduct(productFormData);
            }

            setShowProductModal(false);
            setSelectedProduct(null);
        } catch (err: any) {
            setFormError(err.message || 'Error al guardar producto');
        } finally {
            setFormLoading(false);
        }
    };

    const handleDeleteProduct = async () => {
        if (!productToDelete) return;

        try {
            await deleteProduct(productToDelete.id);
            setShowDeleteConfirm(false);
            setProductToDelete(null);
        } catch (err: any) {
            console.error('Error eliminando producto:', err);
        }
    };

    const addFeature = () => {
        if (newFeature.trim()) {
            setProductFormData(prev => ({
                ...prev,
                features: [...prev.features, newFeature.trim()]
            }));
            setNewFeature('');
        }
    };

    const removeFeature = (index: number) => {
        setProductFormData(prev => ({
            ...prev,
            features: prev.features.filter((_, i) => i !== index)
        }));
    };

    // Funciones para Sales Tools
    const addKeyPoint = () => {
        if (newKeyPoint.trim() && productFormData.salesTools) {
            setProductFormData(prev => ({
                ...prev,
                salesTools: {
                    ...prev.salesTools!,
                    negotiationRules: {
                        ...prev.salesTools!.negotiationRules,
                        keySellingPoints: [...prev.salesTools!.negotiationRules.keySellingPoints, newKeyPoint.trim()]
                    }
                }
            }));
            setNewKeyPoint('');
        }
    };

    const removeKeyPoint = (index: number) => {
        if (productFormData.salesTools) {
            setProductFormData(prev => ({
                ...prev,
                salesTools: {
                    ...prev.salesTools!,
                    negotiationRules: {
                        ...prev.salesTools!.negotiationRules,
                        keySellingPoints: prev.salesTools!.negotiationRules.keySellingPoints.filter((_, i) => i !== index)
                    }
                }
            }));
        }
    };

const generatePaymentQR = async () => {
    if (productFormData.salesTools) {
        let qrData = '';
        
        if (productFormData.salesTools.qrCodes.payment.customPaymentUrl) {
            // Si hay URL personalizada, usar esa
            qrData = productFormData.salesTools.qrCodes.payment.customPaymentUrl;
        } else {
            // Si no, usar datos estructurados
            qrData = JSON.stringify({
                type: 'payment',
                product: productFormData.name,
                amount: productFormData.salesTools.qrCodes.payment.amount,
                currency: productFormData.salesTools.qrCodes.payment.currency,
                method: productFormData.salesTools.qrCodes.payment.paymentMethod
            });
        }
        
        const qrCode = await generateQRCode(qrData, 'payment');
        
        setProductFormData(prev => ({
            ...prev,
            salesTools: {
                ...prev.salesTools!,
                qrCodes: {
                    ...prev.salesTools!.qrCodes,
                    payment: {
                        ...prev.salesTools!.qrCodes.payment,
                        generatedQR: qrCode
                    }
                }
            }
        }));
    }
};

const generateDiscountQR = async () => {
    if (productFormData.salesTools) {
        const discountCode = `DESC${Date.now().toString().slice(-6)}`;
        
        // Para descuentos, usar URL si se proporciona, sino usar código simple
        let qrData = '';
        if (productFormData.salesTools.urls.mainProductUrl) {
            qrData = `${productFormData.salesTools.urls.mainProductUrl}?discount=${discountCode}&value=${productFormData.salesTools.qrCodes.discount.value}`;
        } else {
            qrData = `DESCUENTO: ${discountCode} - ${productFormData.salesTools.qrCodes.discount.value}${productFormData.salesTools.qrCodes.discount.discountType === 'percentage' ? '%' : ' CLP'} OFF`;
        }
        
        const qrCode = await generateQRCode(qrData, 'discount');
        
        setProductFormData(prev => ({
            ...prev,
            salesTools: {
                ...prev.salesTools!,
                qrCodes: {
                    ...prev.salesTools!.qrCodes,
                    discount: {
                        ...prev.salesTools!.qrCodes.discount,
                        code: discountCode,
                        generatedQR: qrCode
                    }
                }
            }
        }));
    }
};

    // ==================== TRADUCIONES - MANTENER ORIGINALES ====================
    const getText = (key: string) => {
        const texts: Record<string, Record<string, string>> = {
            es: {
                title: 'Gestión de Productos',
                addProduct: 'Añadir Producto',
                initDB: 'Inicializar BD',
                loading: 'Cargando...',
                error: 'Error',
                noProducts: 'No hay productos disponibles',
                active: 'Activo',
                inactive: 'Inactivo',
                edit: 'Editar',
                delete: 'Eliminar',
                toggle: 'Cambiar Estado',
                name: 'Nombre',
                description: 'Descripción',
                price: 'Precio',
                category: 'Categoría',
                closingMethod: 'Método de Cierre',
                targetAudience: 'Audiencia Objetivo',
                features: 'Características',
                assignedAgent: 'Agente Asignado',
                save: 'Guardar',
                cancel: 'Cancelar',
                confirmDelete: 'Confirmar eliminación',
                deleteMessage: '¿Estás seguro de que quieres eliminar este producto?',
                addFeature: 'Añadir Característica',
                salesCount: 'Ventas',
                conversionRate: 'Conversión',
                product: 'Producto',
                service: 'Servicio',
                course: 'Curso',
                consultation: 'Consultoría',
                link: 'Enlace',
                qr: 'Código QR',
                form: 'Formulario',
                call: 'Llamada',
                noAgent: 'Sin agente',
                basicInfo: 'Información Básica',
                salesTools: 'Herramientas de Venta',
                aiConfig: 'Configuración IA'
            },
            en: {
                title: 'Product Management',
                addProduct: 'Add Product',
                initDB: 'Initialize DB',
                loading: 'Loading...',
                error: 'Error',
                noProducts: 'No products available',
                active: 'Active',
                inactive: 'Inactive',
                edit: 'Edit',
                delete: 'Delete',
                toggle: 'Toggle Status',
                name: 'Name',
                description: 'Description',
                price: 'Price',
                category: 'Category',
                closingMethod: 'Closing Method',
                targetAudience: 'Target Audience',
                features: 'Features',
                assignedAgent: 'Assigned Agent',
                save: 'Save',
                cancel: 'Cancel',
                confirmDelete: 'Confirm deletion',
                deleteMessage: 'Are you sure you want to delete this product?',
                addFeature: 'Add Feature',
                salesCount: 'Sales',
                conversionRate: 'Conversion',
                product: 'Product',
                service: 'Service',
                course: 'Course',
                consultation: 'Consultation',
                link: 'Link',
                qr: 'QR Code',
                form: 'Form',
                call: 'Call',
                noAgent: 'No agent',
                basicInfo: 'Basic Information',
                salesTools: 'Sales Tools',
                aiConfig: 'AI Configuration'
            }
        };
        return texts[language]?.[key] || key;
    };

    // ==================== RENDER - MANTENER ESTRUCTURA ORIGINAL ====================
    if (productsLoading) {
        return (
            <div className="text-center p-4">
                <Spinner animation="border" />
                <p className="mt-2">{getText('loading')}</p>
            </div>
        );
    }

    if (productsError) {
        return (
            <Alert variant="danger" className="m-3">
                <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                {getText('error')}: {productsError}
                <div className="mt-2">
                    <Button variant="outline-primary" size="sm" onClick={handleInitializeDB} disabled={initializingDB}>
                        <FontAwesomeIcon icon={faDatabase} className="me-1" />
                        {initializingDB ? getText('loading') : getText('initDB')}
                    </Button>
                </div>
            </Alert>
        );
    }

    return (
        <div className="products-tab">
            <style>{customStyles}</style>
            <Row className="mb-3">
                <Col>
                    <div className="d-flex justify-content-between align-items-center">
                        <h4>
                            <FontAwesomeIcon icon={faBoxOpen} className="me-2" />
                            {getText('title')} ({products.length})
                        </h4>
                        <div>
                            <Button 
                                variant="primary" 
                                onClick={() => handleShowProductModal()}
                                className="me-2"
                            >
                                <FontAwesomeIcon icon={faPlus} className="me-1" />
                                {getText('addProduct')}
                            </Button>
                            <Button 
                                variant="outline-secondary" 
                                onClick={() => refetchProducts()} 
                                size="sm"
                            >
                                <FontAwesomeIcon icon={faSync} />
                            </Button>
                        </div>
                    </div>
                </Col>
            </Row>

            {products.length === 0 ? (
                <Card>
                    <Card.Body className="text-center py-4">
                        <FontAwesomeIcon icon={faBoxOpen} size="3x" className="text-muted mb-3" />
                        <h5 className="text-muted">{getText('noProducts')}</h5>
                        <Button variant="primary" onClick={() => handleShowProductModal()}>
                            <FontAwesomeIcon icon={faPlus} className="me-1" />
                            {getText('addProduct')}
                        </Button>
                    </Card.Body>
                </Card>
            ) : (
                <Row>
                    {products.map((product) => (
                        <Col key={product.id} md={6} lg={4} className="mb-3">
                            <Card className="h-100 product-card">
                                <Card.Header className="d-flex justify-content-between align-items-center">
                                    <div className="d-flex align-items-center">
                                        <Badge 
                                            bg={product.isActive ? 'success' : 'secondary'}
                                            className="me-2"
                                        >
                                            {product.isActive ? getText('active') : getText('inactive')}
                                        </Badge>
                                        <small className="text-muted">{product.category}</small>
                                        {product.salesTools && (
                                            <OverlayTrigger
                                                placement="top"
                                                overlay={<Tooltip>Herramientas de venta configuradas</Tooltip>}
                                            >
                                                <Badge bg="info" className="ms-1">
                                                    <FontAwesomeIcon icon={faTools} />
                                                </Badge>
                                            </OverlayTrigger>
                                        )}
                                    </div>
                                    <Dropdown>
                                        <Dropdown.Toggle variant="outline-secondary" size="sm">
                                            <FontAwesomeIcon icon={faCog} />
                                        </Dropdown.Toggle>
                                        <Dropdown.Menu renderOnMount>
                                            <Dropdown.Item onClick={() => handleShowProductModal(product)}>
                                                <FontAwesomeIcon icon={faEdit} className="me-1" />
                                                {getText('edit')}
                                            </Dropdown.Item>
                                            <Dropdown.Item onClick={() => toggleProductStatus(product.id)}>
                                                <FontAwesomeIcon icon={product.isActive ? faPause : faPlay} className="me-1" />
                                                {getText('toggle')}
                                            </Dropdown.Item>
                                            <Dropdown.Divider />
                                            <Dropdown.Item 
                                                className="text-danger"
                                                onClick={() => {
                                                    setProductToDelete(product);
                                                    setShowDeleteConfirm(true);
                                                }}
                                            >
                                                <FontAwesomeIcon icon={faTrash} className="me-1" />
                                                {getText('delete')}
                                            </Dropdown.Item>
                                        </Dropdown.Menu>
                                    </Dropdown>
                                </Card.Header>
                                <Card.Body>
                                    <h6 className="card-title">{product.name}</h6>
                                    <p className="card-text text-muted small">
                                        {product.description.length > 100 
                                            ? `${product.description.substring(0, 100)}...` 
                                            : product.description
                                        }
                                    </p>
                                    <div className="mb-2">
                                        <FontAwesomeIcon icon={faDollarSign} className="me-1" />
                                        <strong>{product.price.toLocaleString()}</strong>
                                    </div>
                                    {product.features.length > 0 && (
                                        <div className="mb-2">
                                            <small className="text-muted d-block">{getText('features')}:</small>
                                            {product.features.slice(0, 3).map((feature, index) => (
                                                <Badge key={index} bg="light" text="dark" className="me-1 mb-1">
                                                    {feature}
                                                </Badge>
                                            ))}
                                            {product.features.length > 3 && (
                                                <Badge bg="secondary">+{product.features.length - 3}</Badge>
                                            )}
                                        </div>
                                    )}
                                    {product.agentName && (
                                        <div className="mb-2">
                                            <FontAwesomeIcon icon={faRobot} className="me-1" />
                                            <small className="text-muted">{product.agentName}</small>
                                        </div>
                                    )}
                                    {/* Preview de Sales Tools */}
                                    {product.salesTools && (
                                        <div className="mt-2 pt-2 border-top">
                                            <div className="d-flex justify-content-between align-items-center">
                                                <small className="text-muted">
                                                    <FontAwesomeIcon icon={faTools} className="me-1" />
                                                    Sales Tools
                                                </small>
                                                <div className="d-flex gap-1">
                                                    {product.salesTools.qrCodes.payment.enabled && (
                                                        <Badge bg="success" className="p-1">
                                                            <FontAwesomeIcon icon={faQrcode} />
                                                        </Badge>
                                                    )}
                                                    {product.salesTools.urls.mainProductUrl && (
                                                        <Badge bg="primary" className="p-1">
                                                            <FontAwesomeIcon icon={faLink} />
                                                        </Badge>
                                                    )}
                                                    {product.salesTools.catalog.images.length > 0 && (
                                                        <Badge bg="warning" className="p-1">
                                                            <FontAwesomeIcon icon={faImage} />
                                                        </Badge>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </Card.Body>
                                <Card.Footer className="text-muted small">
                                    <Row>
                                        <Col>
                                            <FontAwesomeIcon icon={faUsers} className="me-1" />
                                            {getText('salesCount')}: {product.salesCount || 0}
                                        </Col>
                                        <Col>
                                            <FontAwesomeIcon icon={faChartLine} className="me-1" />
                                            {getText('conversionRate')}: {(product.conversionRate || 0).toFixed(1)}%
                                        </Col>
                                    </Row>
                                </Card.Footer>
                            </Card>
                        </Col>
                    ))}
                </Row>
            )}

            {/* Modal para crear/editar producto CON TABS */}
            <Modal show={showProductModal} onHide={() => setShowProductModal(false)} scrollable size="xl">
                <Modal.Header closeButton>
                    <Modal.Title>
                        {selectedProduct ? getText('edit') : getText('addProduct')}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {formError && (
                        <Alert variant="danger" className="mb-3">
                            {formError}
                        </Alert>
                    )}

                    {/* Navegación por tabs */}
                    <Nav variant="tabs" className="mb-3">
                        <Nav.Item>
                            <Nav.Link 
                                active={activeTab === 'basic'} 
                                onClick={() => setActiveTab('basic')}
                            >
                                <FontAwesomeIcon icon={faInfoCircle} className="me-1" />
                                {getText('basicInfo')}
                            </Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                            <Nav.Link 
                                active={activeTab === 'salestools'} 
                                onClick={() => setActiveTab('salestools')}
                            >
                                <FontAwesomeIcon icon={faTools} className="me-1" />
                                {getText('salesTools')}
                            </Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                            <Nav.Link 
                                active={activeTab === 'aiconfig'} 
                                onClick={() => setActiveTab('aiconfig')}
                            >
                                <FontAwesomeIcon icon={faBrain} className="me-1" />
                                {getText('aiConfig')}
                            </Nav.Link>
                        </Nav.Item>
                    </Nav>

                    {/* CONTENIDO DE LOS TABS */}
                    {activeTab === 'basic' && (
                        <Form>
                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>{getText('name')}</Form.Label>
                                        <Form.Control
                                            type="text"
                                            value={productFormData.name}
                                            onChange={(e) => setProductFormData(prev => ({
                                                ...prev,
                                                name: e.target.value
                                            }))}
                                            required
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>{getText('price')}</Form.Label>
                                        <InputGroup>
                                            <InputGroup.Text>$</InputGroup.Text>
                                            <Form.Control
                                                type="number"
                                                value={productFormData.price}
                                                onChange={(e) => {
                                                    setProductFormData(prev => {
                                                        const newPrice = e.target.value;
                                                        return {
                                                            ...prev,
                                                            price: newPrice,
                                                            salesTools: prev.salesTools ? {
                                                                ...prev.salesTools,
                                                                qrCodes: {
                                                                    ...prev.salesTools.qrCodes,
                                                                    payment: {
                                                                        ...prev.salesTools.qrCodes.payment,
                                                                        amount: Number(newPrice) || 0
                                                                    }
                                                                },
                                                                negotiationRules: {
                                                                    ...prev.salesTools.negotiationRules,
                                                                    minPrice: Math.floor(Number(newPrice) * 0.8) || 0
                                                                }
                                                            } : prev.salesTools
                                                        };
                                                    });
                                                }}
                                                required
                                            />
                                        </InputGroup>
                                    </Form.Group>
                                </Col>
                            </Row>
                            
                            <Form.Group className="mb-3">
                                <Form.Label>{getText('description')}</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={3}
                                    value={productFormData.description}
                                    onChange={(e) => setProductFormData(prev => ({
                                        ...prev,
                                        description: e.target.value
                                    }))}
                                    required
                                />
                            </Form.Group>

                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>{getText('category')}</Form.Label>
                                        <Form.Select
                                            value={productFormData.category}
                                            onChange={(e) => setProductFormData(prev => ({
                                                ...prev,
                                                category: e.target.value
                                            }))}
                                        >
                                            <option value="product">{getText('product')}</option>
                                            <option value="service">{getText('service')}</option>
                                            <option value="course">{getText('course')}</option>
                                            <option value="consultation">{getText('consultation')}</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>{getText('closingMethod')}</Form.Label>
                                        <Form.Select
                                            value={productFormData.closingMethod}
                                            onChange={(e) => setProductFormData(prev => ({
                                                ...prev,
                                                closingMethod: e.target.value
                                            }))}
                                        >
                                            <option value="link">{getText('link')}</option>
                                            <option value="qr">{getText('qr')}</option>
                                            <option value="form">{getText('form')}</option>
                                            <option value="call">{getText('call')}</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                            </Row>

                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>{getText('targetAudience')}</Form.Label>
                                        <Form.Control
                                            type="text"
                                            value={productFormData.targetAudience}
                                            onChange={(e) => setProductFormData(prev => ({
                                                ...prev,
                                                targetAudience: e.target.value
                                            }))}
                                            placeholder="Ej: Empresarios, Estudiantes, Profesionales"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>{getText('assignedAgent')}</Form.Label>
                                        <Form.Select
                                            value={productFormData.assignedAgent}
                                            onChange={(e) => setProductFormData(prev => ({
                                                ...prev,
                                                assignedAgent: e.target.value
                                            }))}
                                        >
                                            <option value="">{getText('noAgent')}</option>
                                            {agents.map((agent) => (
                                                <option key={agent.id} value={agent.id}>
                                                    {agent.name} ({agent.personality})
                                                </option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                            </Row>

                            <Form.Group className="mb-3">
                                <Form.Label>{getText('features')}</Form.Label>
                                <div className="mb-2">
                                    {productFormData.features.map((feature, index) => (
                                        <Badge 
                                            key={index} 
                                            bg="primary" 
                                            className="me-1 mb-1 position-relative"
                                            style={{ paddingRight: '25px' }}
                                        >
                                            {feature}
                                            <Button
                                                variant="link"
                                                size="sm"
                                                className="position-absolute top-0 end-0 p-0 text-white"
                                                style={{ fontSize: '0.7rem', lineHeight: '1' }}
                                                onClick={() => removeFeature(index)}
                                            >
                                                <FontAwesomeIcon icon={faTimes} />
                                            </Button>
                                        </Badge>
                                    ))}
                                </div>
                                <InputGroup>
                                    <Form.Control
                                        type="text"
                                        value={newFeature}
                                        onChange={(e) => setNewFeature(e.target.value)}
                                        placeholder={getText('addFeature')}
                                        onKeyPress={(e) => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault();
                                                addFeature();
                                            }
                                        }}
                                    />
                                    <Button 
                                        variant="outline-primary" 
                                        onClick={addFeature}
                                        disabled={!newFeature.trim()}
                                    >
                                        <FontAwesomeIcon icon={faPlus} />
                                    </Button>
                                </InputGroup>
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Check
                                    type="checkbox"
                                    label={getText('active')}
                                    checked={productFormData.isActive}
                                    onChange={(e) => setProductFormData(prev => ({
                                        ...prev,
                                        isActive: e.target.checked
                                    }))}
                                />
                            </Form.Group>
                        </Form>
                    )}

                    {/* TAB SALES TOOLS */}
                    {activeTab === 'salestools' && (
                        <div>
                            <Row className="g-4">
                                {/* URLs y Enlaces */}
                                <Col xs={12} lg={6}>
                                    <Card className="h-100">
                                        <Card.Header>
                                            <h6 className="mb-0">
                                                <FontAwesomeIcon icon={faLink} className="me-2 text-primary" />
                                                URLs y Enlaces
                                            </h6>
                                        </Card.Header>
                                        <Card.Body>
                                            <Form.Group className="mb-3">
                                                <Form.Label className="small fw-semibold">URL Principal</Form.Label>
                                                <InputGroup>
                                                    <InputGroup.Text><FontAwesomeIcon icon={faGlobe} /></InputGroup.Text>
                                                    <Form.Control
                                                        type="url"
                                                        value={productFormData.salesTools?.urls.mainProductUrl || ''}
                                                        onChange={(e) => setProductFormData(prev => ({
                                                            ...prev,
                                                            salesTools: {
                                                                ...prev.salesTools!,
                                                                urls: {
                                                                    ...prev.salesTools!.urls,
                                                                    mainProductUrl: e.target.value
                                                                }
                                                            }
                                                        }))}
                                                        placeholder="https://ejemplo.com/producto"
                                                    />
                                                </InputGroup>
                                            </Form.Group>

                                            <Form.Group className="mb-3">
                                                <Form.Label className="small fw-semibold">URL Demo</Form.Label>
                                                <InputGroup>
                                                    <InputGroup.Text><FontAwesomeIcon icon={faEye} /></InputGroup.Text>
                                                    <Form.Control
                                                        type="url"
                                                        value={productFormData.salesTools?.urls.demoUrl || ''}
                                                        onChange={(e) => setProductFormData(prev => ({
                                                            ...prev,
                                                            salesTools: {
                                                                ...prev.salesTools!,
                                                                urls: {
                                                                    ...prev.salesTools!.urls,
                                                                    demoUrl: e.target.value
                                                                }
                                                            }
                                                        }))}
                                                        placeholder="https://demo.ejemplo.com"
                                                    />
                                                </InputGroup>
                                            </Form.Group>
                                        </Card.Body>
                                    </Card>
                                </Col>

                                {/* Códigos QR */}
                                <Col xs={12} lg={6}>
                                    <Card className="h-100">
                                        <Card.Header>
                                            <h6 className="mb-0">
                                                <FontAwesomeIcon icon={faQrcode} className="me-2 text-success" />
                                                Códigos QR
                                            </h6>
                                        </Card.Header>
                                        <Card.Body>
{/* QR de Descuento */}
<div className="border rounded p-3">
    <div className="d-flex justify-content-between align-items-center mb-2">
        <h6 className="mb-0">
            <FontAwesomeIcon icon={faPercent} className="me-1" />
            QR de Descuento
        </h6>
        <Form.Check
            type="switch"
            checked={productFormData.salesTools?.qrCodes.discount.enabled || false}
            onChange={(e) => setProductFormData(prev => ({
                ...prev,
                salesTools: {
                    ...prev.salesTools!,
                    qrCodes: {
                        ...prev.salesTools!.qrCodes,
                        discount: {
                            ...prev.salesTools!.qrCodes.discount,
                            enabled: e.target.checked
                        }
                    }
                }
            }))}
        />
    </div>

    {productFormData.salesTools?.qrCodes.discount.enabled && (
        <>
            {/* Inputs de configuración que ya tienes */}
            
            {/* BOTÓN PARA GENERAR QR - ESTO ES LO QUE FALTABA */}
            <Button 
                variant="outline-warning" 
                size="sm"
                onClick={generateDiscountQR}
                className="mb-2"
            >
                <FontAwesomeIcon icon={faQrcode} className="me-1" />
                Generar QR
            </Button>

            {/* Código generado y QR con descarga */}
            {productFormData.salesTools.qrCodes.discount.code && (
                <Alert variant="info" className="py-2 mb-2">
                    <small>
                        <strong>Código:</strong> {productFormData.salesTools.qrCodes.discount.code}
                    </small>
                </Alert>
            )}

            {productFormData.salesTools.qrCodes.discount.generatedQR && (
                <div className="text-center">
                    <img 
                        src={productFormData.salesTools.qrCodes.discount.generatedQR} 
                        alt="QR Descuento" 
                        style={{ maxWidth: '150px', border: '2px dashed #dee2e6', borderRadius: '8px', padding: '1rem' }}
                    />
                    <div className="mt-2">
                        <Button 
                            variant="outline-warning" 
                            size="sm"
                            onClick={() => downloadQRCode(
                                productFormData.salesTools!.qrCodes.discount.generatedQR!,
                                `qr-descuento-${productFormData.name.replace(/\s+/g, '-').toLowerCase()}.png`
                            )}
                        >
                            <FontAwesomeIcon icon={faDownload} className="me-1" />
                            Descargar QR
                        </Button>
                    </div>
                </div>
            )}
        </>
    )}
</div>
                                        </Card.Body>
                                    </Card>
                                </Col>
                            </Row>
                        </div>
                    )}

                    {/* TAB AI CONFIG */}
                    {activeTab === 'aiconfig' && (
                        <div>
                            <Card>
                                <Card.Header>
                                    <h6 className="mb-0">
                                        <FontAwesomeIcon icon={faBrain} className="me-2 text-info" />
                                        Configuración de Negociación IA
                                    </h6>
                                </Card.Header>
                                <Card.Body>
                                    <Row className="g-3 mb-3">
                                        <Col xs={12} md={4}>
                                            <Form.Group>
                                                <Form.Label className="small fw-semibold">Precio Mínimo</Form.Label>
                                                <InputGroup size="sm">
                                                    <InputGroup.Text>$</InputGroup.Text>
                                                    <Form.Control
                                                        type="number"
                                                        value={productFormData.salesTools?.negotiationRules.minPrice || 0}
                                                        onChange={(e) => setProductFormData(prev => ({
                                                            ...prev,
                                                            salesTools: {
                                                                ...prev.salesTools!,
                                                                negotiationRules: {
                                                                    ...prev.salesTools!.negotiationRules,
                                                                    minPrice: Number(e.target.value)
                                                                }
                                                            }
                                                        }))}
                                                    />
                                                </InputGroup>
                                                <Form.Text className="text-muted">
                                                    Precio mínimo que puede aceptar la IA
                                                </Form.Text>
                                            </Form.Group>
                                        </Col>
                                        <Col xs={12} md={4}>
                                            <Form.Group>
                                                <Form.Label className="small fw-semibold">Descuento Máximo (%)</Form.Label>
                                                <InputGroup size="sm">
                                                    <Form.Control
                                                        type="number"
                                                        value={productFormData.salesTools?.negotiationRules.maxDiscount || 0}
                                                        onChange={(e) => setProductFormData(prev => ({
                                                            ...prev,
                                                            salesTools: {
                                                                ...prev.salesTools!,
                                                                negotiationRules: {
                                                                    ...prev.salesTools!.negotiationRules,
                                                                    maxDiscount: Number(e.target.value)
                                                                }
                                                            }
                                                        }))}
                                                        max="50"
                                                    />
                                                    <InputGroup.Text>%</InputGroup.Text>
                                                </InputGroup>
                                            </Form.Group>
                                        </Col>
                                        <Col xs={12} md={4}>
                                            <Form.Group>
                                                <Form.Label className="small fw-semibold">Estilo de Negociación</Form.Label>
                                                <Form.Select 
                                                    size="sm"
                                                    value={productFormData.salesTools?.negotiationRules.negotiationStyle || 'flexible'}
                                                    onChange={(e) => setProductFormData(prev => ({
                                                        ...prev,
                                                        salesTools: {
                                                            ...prev.salesTools!,
                                                            negotiationRules: {
                                                                ...prev.salesTools!.negotiationRules,
                                                                negotiationStyle: e.target.value as any
                                                            }
                                                        }
                                                    }))}
                                                >
                                                    <option value="flexible">Flexible</option>
                                                    <option value="firm">Firme</option>
                                                    <option value="aggressive">Agresivo</option>
                                                </Form.Select>
                                            </Form.Group>
                                        </Col>
                                    </Row>

                                    {/* Puntos Clave de Venta */}
                                    <div className="mb-4">
                                        <h6 className="mb-2">
                                            <FontAwesomeIcon icon={faLightbulb} className="me-1" />
                                            Puntos Clave de Venta
                                        </h6>
                                        
                                        {productFormData.salesTools?.negotiationRules.keySellingPoints.length! > 0 && (
                                            <div className="mb-2">
                                                <ListGroup variant="flush">
                                                    {productFormData.salesTools?.negotiationRules.keySellingPoints.map((point, index) => (
                                                        <ListGroup.Item key={index} className="d-flex justify-content-between align-items-center py-2">
                                                            <span className="small">{point}</span>
                                                            <Button 
                                                                variant="outline-danger" 
                                                                size="sm"
                                                                onClick={() => removeKeyPoint(index)}
                                                            >
                                                                <FontAwesomeIcon icon={faTimes} />
                                                            </Button>
                                                        </ListGroup.Item>
                                                    ))}
                                                </ListGroup>
                                            </div>
                                        )}

                                        <InputGroup size="sm">
                                            <Form.Control
                                                type="text"
                                                value={newKeyPoint}
                                                onChange={(e) => setNewKeyPoint(e.target.value)}
                                                placeholder="Añadir punto clave"
                                                onKeyPress={(e) => {
                                                    if (e.key === 'Enter') {
                                                        e.preventDefault();
                                                        addKeyPoint();
                                                    }
                                                }}
                                            />
                                            <Button 
                                                variant="outline-primary" 
                                                onClick={addKeyPoint}
                                                disabled={!newKeyPoint.trim()}
                                            >
                                                <FontAwesomeIcon icon={faPlus} />
                                            </Button>
                                        </InputGroup>
                                        <Form.Text className="text-muted">
                                            Puntos que la IA debe destacar durante la venta
                                        </Form.Text>
                                    </div>

                                    {/* Resumen de Configuración */}
                                    <Alert variant="info" className="mt-3">
                                        <h6 className="mb-2">
                                            <FontAwesomeIcon icon={faInfoCircle} className="me-1" />
                                            Resumen de Configuración
                                        </h6>
                                        <Row className="g-2">
                                            <Col xs={6}>
                                                <small className="text-muted d-block">Precio Base:</small>
                                                <strong className="text-success">
                                                    ${Number(productFormData.price || 0).toLocaleString()}
                                                </strong>
                                            </Col>
                                            <Col xs={6}>
                                                <small className="text-muted d-block">Precio Mínimo:</small>
                                                <strong>
                                                    ${(productFormData.salesTools?.negotiationRules.minPrice || 0).toLocaleString()}
                                                </strong>
                                            </Col>
                                            <Col xs={6}>
                                                <small className="text-muted d-block">Descuento Máximo:</small>
                                                <strong>{productFormData.salesTools?.negotiationRules.maxDiscount || 0}%</strong>
                                            </Col>
                                            <Col xs={6}>
                                                <small className="text-muted d-block">Estilo:</small>
                                                <Badge bg="info">
                                                    {productFormData.salesTools?.negotiationRules.negotiationStyle || 'flexible'}
                                                </Badge>
                                            </Col>
                                        </Row>
                                        <hr />
                                        <small>
                                            <FontAwesomeIcon icon={faInfoCircle} className="me-1" />
                                            La IA usará esta configuración para vender de manera autónoma y profesional.
                                        </small>
                                    </Alert>
                                </Card.Body>
                            </Card>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button 
                        variant="secondary" 
                        onClick={() => setShowProductModal(false)}
                        disabled={formLoading}
                    >
                        {getText('cancel')}
                    </Button>
                    <Button 
                        variant="primary" 
                        onClick={handleSaveProduct}
                        disabled={formLoading || !productFormData.name || !productFormData.price}
                    >
                        {formLoading ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-1" />
                                {getText('loading')}
                            </>
                        ) : (
                            <>
                                <FontAwesomeIcon icon={faCheck} className="me-1" />
                                {getText('save')}
                            </>
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Modal de confirmación de eliminación */}
            <Modal show={showDeleteConfirm} onHide={() => setShowDeleteConfirm(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>{getText('confirmDelete')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="text-center">
                        <FontAwesomeIcon icon={faExclamationTriangle} size="3x" className="text-warning mb-3" />
                        <p>{getText('deleteMessage')}</p>
                        {productToDelete && (
                            <div className="p-3 bg-light rounded">
                                <strong className="d-block">{productToDelete.name}</strong>
                                <small className="text-muted">
                                    ${productToDelete.price.toLocaleString()} - {productToDelete.category}
                                </small>
                                {productToDelete.salesTools && (
                                    <div className="mt-2">
                                        <Badge bg="warning" className="small">
                                            <FontAwesomeIcon icon={faTools} className="me-1" />
                                            Con herramientas de venta configuradas
                                        </Badge>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button 
                        variant="secondary" 
                        onClick={() => setShowDeleteConfirm(false)}
                    >
                        {getText('cancel')}
                    </Button>
                    <Button 
                        variant="danger" 
                        onClick={handleDeleteProduct}
                    >
                        <FontAwesomeIcon icon={faTrash} className="me-1" />
                        {getText('delete')}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default ProductsTab;